import React, { useEffect, useRef, lazy, Suspense } from "react";
import ReactDOM from "react-dom";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import { closeGlobalModal } from "../globalModalSlice";
import Icon from "../../../components/Icon/Icon";
import Skeleton from "react-loading-skeleton";

// --- Lazy-loaded Content Components ---
const componentMap = {
  applyCoupon: lazy(
    () =>
      import("../../../../templates/ecommerce/cart/components/common/CartApplyCouponForm")
  ),
  shippingCalculator: lazy(
    () =>
      import("../../../../templates/ecommerce/cart/components/common/CartShippingCalculator")
  ),
  legalAgreements: lazy(
    () =>
      import("../../../../templates/ecommerce/checkout/components/PaymentStep/LegalAgreementsModal")
  ),
  paymentForm: lazy(
    () =>
      import("../../../../templates/ecommerce/checkout/components/PaymentStep/PaymentForm")
  ),
  stockNotification: lazy(
    () =>
      import("../../../../templates/ecommerce/stockNotification/components/StockNotificationForm")
  ),
  cookiePreferences: lazy(
    () => import("../../cookieConsent/components/CookiePreferencesModal")
  ),
};

const GlobalModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, title, contentKey, contentProps } = useAppSelector(
    (state) => state.globalModal
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const portalRoot = document.body;

  const handleClose = () => {
    dispatch(closeGlobalModal());
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };

    if (isOpen) {
      document.body.classList.add("global-modal-open");
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.body.classList.remove("global-modal-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleClose]);

  if (!isOpen || !portalRoot) {
    return null;
  }

  const ContentComponent = contentKey ? componentMap[contentKey] : null;

  return ReactDOM.createPortal(
    <div
      className={`global-modal-wrapper ${isOpen ? "is-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div className="global-modal__overlay" onClick={handleClose} />
      <div
        ref={panelRef}
        className="global-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="global-modal-title"
      >
        <div className="global-modal__header">
          <h2 id="global-modal-title" className="global-modal__title">
            {title}
          </h2>
          <button
            onClick={handleClose}
            className="global-modal__close-btn"
            aria-label="Close modal"
          >
            <Icon iconName="x-mark" className="icon--stroke-thickest" />
          </button>
        </div>
        <div className="global-modal__body">
          <Suspense fallback={<Skeleton height={200} />}>
            {ContentComponent ? <ContentComponent {...contentProps} /> : null}
          </Suspense>
        </div>
      </div>
    </div>,
    portalRoot
  );
};

export default GlobalModal;
